/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-23
 */

import { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  PersonOutline as PersonIcon,
  AdminPanelSettings as AdminIcon,
  SupervisorAccount as ManagerIcon,
  Star as OwnerIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';

// Add local dependencies.
import { StudioRole } from '../../../types/studio';
import { MemberActionMenuProps } from '../../../types/studioMembers';
import { useStudioPermissions } from '../../../hooks/useStudioPermissions';
import {
  canManageUser,
  getAssignableRoles,
  getRoleDisplayName,
} from '../../../utils/permissions';

const MemberActionMenu = ({
  member,
  onRoleChange,
  onRemoveMember,
  onViewProfile,
  currentUserRole: _currentUserRole,
}: MemberActionMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // Use the new permission system
  const { role: userRole } = useStudioPermissions();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    // Prevent event from bubbling up to table row
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRoleChange = (newRole: StudioRole) => {
    onRoleChange(newRole);
    handleClose();
  };

  const handleRemoveMember = () => {
    onRemoveMember();
    handleClose();
  };

  const handleViewProfile = () => {
    onViewProfile();
    handleClose();
  };

  // Permission checks using new permission system
  const canChangeRoleForMember = canManageUser(userRole, member.role);
  const canRemoveMemberFromStudio = canManageUser(userRole, member.role);

  const getRoleIcon = (role: StudioRole) => {
    switch (role) {
      case StudioRole.OWNER:
        return <OwnerIcon fontSize="small" />;
      case StudioRole.MANAGER:
        return <ManagerIcon fontSize="small" />;
      case StudioRole.ADMIN:
        return <AdminIcon fontSize="small" />;
      case StudioRole.MEMBER:
        return <PersonIcon fontSize="small" />;
      default:
        return <PersonIcon fontSize="small" />;
    }
  };

  // Get available roles using new permission system
  const availableRoles = getAssignableRoles(userRole).filter(
    role => role !== member.role
  );

  return (
    <>
      <IconButton
        aria-label="Member actions"
        aria-controls={open ? 'member-action-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        size="small"
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        id="member-action-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        // The menu is portalled, but React still bubbles its clicks (items
        // and backdrop) to the table row, which would open the profile.
        onClick={event => event.stopPropagation()}
        MenuListProps={{
          'aria-labelledby': 'member-action-button',
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* View Profile */}
        <MenuItem onClick={handleViewProfile}>
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Profile</ListItemText>
        </MenuItem>

        {/* Send Message */}
        {/* <MenuItem onClick={handleSendMessage}>
          <ListItemIcon>
            <MessageIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Send Message</ListItemText>
        </MenuItem> */}

        {/* Role Changes */}
        {canChangeRoleForMember &&
          availableRoles.length > 0 && [
            <Divider key="role-divider" />,
            ...availableRoles.map(role => (
              <MenuItem key={role} onClick={() => handleRoleChange(role)}>
                <ListItemIcon>{getRoleIcon(role)}</ListItemIcon>
                <ListItemText>
                  Change to {getRoleDisplayName(role)}
                </ListItemText>
              </MenuItem>
            )),
          ]}

        {/* Remove Member */}
        {canRemoveMemberFromStudio && [
          <Divider key="remove-divider" />,
          <MenuItem
            key="remove-member"
            onClick={handleRemoveMember}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Remove Member</ListItemText>
          </MenuItem>,
        ]}
      </Menu>
    </>
  );
};

export default MemberActionMenu;
